import { useState } from 'react'
import { FileText, Copy, Check } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function TextFormatter() {
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [copied, setCopied] = useState(false)

  const formatText = (action) => {
    if (!input.trim()) {
      alert('Please enter some text')
      return
    }

    let formatted = input

    switch (action) {
      case 'trim':
        formatted = input.trim()
        break
      case 'trim-lines':
        formatted = input.split('\n').map(line => line.trim()).join('\n')
        break
      case 'remove-empty-lines':
        formatted = input.split('\n').filter(line => line.trim() !== '').join('\n')
        break
      case 'remove-extra-spaces':
        formatted = input.replace(/\s+/g, ' ').trim()
        break
      case 'remove-line-breaks':
        formatted = input.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()
        break
      case 'add-line-breaks':
        formatted = input.replace(/\. /g, '.\n').replace(/! /g, '!\n').replace(/\? /g, '?\n')
        break
      case 'uppercase':
        formatted = input.toUpperCase()
        break
      case 'lowercase':
        formatted = input.toLowerCase()
        break
      case 'capitalize':
        formatted = input.split(' ').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ')
        break
      case 'sentence-case':
        formatted = input.toLowerCase().replace(/^\w|\. \w/g, (match) => match.toUpperCase())
        break
      case 'inverse-case':
        formatted = input.split('').map(char => 
          char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()
        ).join('')
        break
      default:
        formatted = input
    }

    setOutput(formatted)
  }

  const copyToClipboard = () => {
    if (output) {
      navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const clearAll = () => {
    setInput('')
    setOutput('')
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Enter text to format:</label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="input w-full min-h-[200px]"
              placeholder="Paste or type your text here..."
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
            <Button onClick={() => formatText('trim')} variant="outline" size="sm">
              Trim
            </Button>
            <Button onClick={() => formatText('trim-lines')} variant="outline" size="sm">
              Trim Lines
            </Button>
            <Button onClick={() => formatText('remove-empty-lines')} variant="outline" size="sm">
              Remove Empty Lines
            </Button>
            <Button onClick={() => formatText('remove-extra-spaces')} variant="outline" size="sm">
              Remove Extra Spaces
            </Button>
            <Button onClick={() => formatText('remove-line-breaks')} variant="outline" size="sm">
              Remove Line Breaks
            </Button>
            <Button onClick={() => formatText('add-line-breaks')} variant="outline" size="sm">
              Add Line Breaks
            </Button>
            <Button onClick={() => formatText('uppercase')} variant="outline" size="sm">
              UPPERCASE
            </Button>
            <Button onClick={() => formatText('lowercase')} variant="outline" size="sm">
              lowercase
            </Button>
            <Button onClick={() => formatText('capitalize')} variant="outline" size="sm">
              Capitalize
            </Button>
            <Button onClick={() => formatText('sentence-case')} variant="outline" size="sm">
              Sentence Case
            </Button>
            <Button onClick={() => formatText('inverse-case')} variant="outline" size="sm">
              Inverse Case
            </Button>
            <Button onClick={clearAll} variant="outline" size="sm">
              Clear All
            </Button>
          </div>
        </Card.Body>
      </Card>

      {output && (
        <Card>
          <Card.Body>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-gray-700 font-semibold">Formatted text:</label>
                <Button onClick={copyToClipboard} variant="outline" size="sm">
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <textarea
                value={output}
                readOnly
                className="input w-full min-h-[200px] bg-gray-50"
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {!output && (
        <Card>
          <Card.Body className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Enter text and select a formatting option above</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

